import React, { ReactNode, useEffect, useLayoutEffect } from 'react';
import { Route, Redirect, RouteProps, RouteComponentProps, useHistory, useLocation } from 'react-router-dom';
import { useEventPrivateInfo } from '../../../hooks/api/protected/use-event-private-details';
import { useEventPublicInfo } from '../../../hooks/api/public/use-event-public-details';
import { IEventCustomSettings, useCustomSettings } from '../../../hooks/use-custom-settings';
import { useAuth, IAuthContext } from '../../../lib/context-providers/auth-context';
import { HTTP_STATUS } from '../../api';
import { RoutesConfig } from '../../routes';
import { NOT_FOUND_PATH, REGISTRATION_PATH, ROOT, SSO_LOGIN_PATH } from '../../routes/paths';
import useDefaultRoutePage from '../../../hooks/use-default-route-page';
import { normalizeRoutePath } from '../../../lib/helpers/urlHelper';
import { usePathPrefix } from '../../../hooks/use-path-prefix';
import DefaultSpinner from '../../../app/shared/DefaultSpinner';
import { CODE, STATE, TOKEN } from '../../../lib/constants';

/* ALLOWED ONLY FOR AUTHENTICATED USER  */
export const ProtectedRoute: React.FC<RoutesConfig> = ({ component, ...rest }: RoutesConfig): JSX.Element => {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth() as IAuthContext;
  const pathPrefix = usePathPrefix();
  const rootPath = normalizeRoutePath(ROOT, pathPrefix);

  const Component = component as React.FC;
  const is404 = [useEventPublicInfo(), useEventPrivateInfo()]
    .some(({ error }) => error?.status === HTTP_STATUS.NOT_FOUND);

  if (is404) return <Redirect to={NOT_FOUND_PATH} />;
  if (isLoading) return <DefaultSpinner visible={isLoading} />;

  return (
    <Route
      {...rest}
      render={(props: RouteProps) => {
        if (isAuthenticated === null) return null;
        if (isAuthenticated) return <Component {...props} />;

        return (
          <Redirect
            to={{
              pathname: rootPath,
              state: { ...location.state as object, from: location.pathname },
              search: location.search
            }}
          />
        );
      }}
    />);
};

const ReplaceAfterCommit: React.FC<{ to: string; onlyFrom: string }> = ({ to, onlyFrom }): null => {
  const history = useHistory();

  useEffect(() => {
    const currentPath = history.location?.pathname;
    if (currentPath && currentPath !== onlyFrom) {
      return;
    }
    history.replace(to);
  }, [history, onlyFrom, to]);

  return null;
};

/* ALLOWED FOR AUTHENTICATED AND NON-AUTHENTICATED USER */
export const RouteWithRedirect: React.FC<RoutesConfig> = ({ component, ...rest }: RoutesConfig): JSX.Element => {
  const auth = useAuth() as IAuthContext;
  const Component = component as React.FC<RouteComponentProps>;
  const defaultRoutePage = useDefaultRoutePage();
  const pathPrefix = usePathPrefix();
  const rootPath = normalizeRoutePath(ROOT, pathPrefix);

  const render = (props: RouteComponentProps) => {
    const { match: { path, isExact }, location } = props;
    /* skip redirect to itself: default route page falls back to the root path until private event details are loaded */
    const shouldRedirectToDefaultPage = !!defaultRoutePage && defaultRoutePage !== location.pathname;
    const hasPendingRegistrationToken = !!new URLSearchParams(location.search).get(TOKEN);
    const redirectToDefaultPage = !!auth.isAuthenticated && path === rootPath && isExact
      && shouldRedirectToDefaultPage && !hasPendingRegistrationToken;

    return (
      <>
        {redirectToDefaultPage && <ReplaceAfterCommit to={defaultRoutePage} onlyFrom={rootPath} />}
        <Component {...props} />
      </>
    );
  };

  return (
    <Route {...{ ...rest, render }} />
  );
};

/**
 * Clears Passport tokens once on mount so a public route (SSO / registration callback) can run
 * without inheriting an existing session. Does not navigate away — URL query params are preserved.
 */
const PublicSessionReset: React.FC = (): JSX.Element => {
  const auth = useAuth() as IAuthContext;

  useLayoutEffect(() => {
    auth.clearLocalSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <DefaultSpinner visible />;
};

const shouldResetSessionOnPublicRoute = (
  pathname: string,
  search: string,
  pathPrefix: string
): boolean => {
  const ssoLoginFull = normalizeRoutePath(SSO_LOGIN_PATH, pathPrefix);
  if (pathname === ssoLoginFull) {
    return true;
  }

  const registrationBase = normalizeRoutePath(REGISTRATION_PATH, pathPrefix);
  const isRegistrationRoute =
    pathname === registrationBase || pathname.startsWith(`${registrationBase}/`);
  if (!isRegistrationRoute) {
    return false;
  }

  const params = new URLSearchParams(search);
  return Boolean(params.get(CODE) && params.get(STATE));
};

/* ALLOWED ONLY FOR NON-AUTHENTICATED USER */
export const PublicRoute: React.FC<RoutesConfig> = ({ component, ...rest }: RoutesConfig): JSX.Element => {
  const { isAuthenticated } = useAuth() as IAuthContext;
  const { nonExisted } = useCustomSettings() as IEventCustomSettings;
  const defaultRoutePage = useDefaultRoutePage();
  const pathPrefix = usePathPrefix();
  const Component = component as React.FC<RouteComponentProps>;

  const render = (props: RouteComponentProps): ReactNode => {
    if (nonExisted) return <Redirect to={NOT_FOUND_PATH} />;
    if (isAuthenticated === false && props.match.isExact) return <Component {...props} />;
    if (isAuthenticated) {
      const { pathname, search } = props.location;
      if (shouldResetSessionOnPublicRoute(pathname, search, pathPrefix)) {
        return <PublicSessionReset />;
      }
      return <Redirect to={defaultRoutePage} />;
    }
  };

  return (
    <Route {...{ ...rest, render }} />
  );
};

export const PassportRoute: React.FC<RoutesConfig> = ({ _protected, _public, ...rest }: RoutesConfig): JSX.Element => {
  if (_protected) return <ProtectedRoute { ...rest } />;
  if (_public) return <PublicRoute { ...rest } />;
  return <RouteWithRedirect { ...rest } />;
};
